// Workspace API helpers — thin wrapper over the shared `api` client.
// Central place so components don't hardcode paths + response shapes.
import api from "../../lib/api";

export const workspaceApi = {
  // Config
  getConfig:      ()                        => api.get("/workspace/config").then(r => r.data),
  patchConfig:    (payload)                 => api.patch("/workspace/config", payload).then(r => r.data),
  // Teams
  listTeams:      (scope = "mine")          => api.get(`/workspace/teams?scope=${scope}`).then(r => r.data),
  createTeam:     (payload)                 => api.post("/workspace/teams", payload).then(r => r.data),
  patchTeam:      (id, payload)             => api.patch(`/workspace/teams/${id}`, payload).then(r => r.data),
  editMembership: (id, add = [], remove = []) =>
    api.post(`/workspace/teams/${id}/members`, { add, remove }).then(r => r.data),
  // Tasks
  listTasks:      (params = {})             => api.get("/workspace/tasks", { params }).then(r => r.data),
  createTask:     (payload)                 => api.post("/workspace/tasks", payload).then(r => r.data),
  getTask:        (id)                      => api.get(`/workspace/tasks/${id}`).then(r => r.data),
  patchTask:      (id, payload)             => api.patch(`/workspace/tasks/${id}`, payload).then(r => r.data),
  deleteTask:     (id)                      => api.delete(`/workspace/tasks/${id}`).then(r => r.data),
  claimTask:      (id)                      => api.post(`/workspace/tasks/${id}/claim`).then(r => r.data),
  addComment:     (id, text)                => api.post(`/workspace/tasks/${id}/comments`, { text }).then(r => r.data),
  batchPatch:     (task_ids, patch)         => api.post("/workspace/tasks/batch/patch", { task_ids, patch }).then(r => r.data),
  batchDelete:    (task_ids)                => api.post("/workspace/tasks/batch/delete", { task_ids }).then(r => r.data),
  // Cockpit / inbox / export / import
  home:           (userId = null)           => api.get("/workspace/home", { params: userId ? { user_id: userId } : {} }).then(r => r.data),
  inbox:          ()                        => api.get("/workspace/inbox").then(r => r.data),
  exportCsvUrl:   (params = {})             => {
    const search = new URLSearchParams(params).toString();
    return `${process.env.REACT_APP_BACKEND_URL}/api/workspace/export${search ? "?" + search : ""}`;
  },
  importTasks:    (rows)                    => api.post("/workspace/tasks/import", rows).then(r => r.data),
  taskActivity:   (id)                      => api.get(`/workspace/tasks/${id}/activity`).then(r => r.data),
  linkedToMe:     ()                        => api.get("/workspace/linked-to-me").then(r => r.data),
  // Org chart + admin audit
  orgChart:       ()                        => api.get("/workspace/org-chart").then(r => r.data),
  patchOrgChart:  (payload)                 => api.patch("/workspace/org-chart", payload).then(r => r.data),
  adminLog:       (params = {})             => api.get("/workspace/admin-log", { params }).then(r => r.data),
  // Full Squad (org directory)
  fullSquad:      ()                        => api.get("/workspace/full-squad").then(r => r.data),
  patchUser:      (id, payload)             => api.patch(`/workspace/full-squad/${id}`, payload).then(r => r.data),
  // Team comments (patchTeam is declared once above under the Teams block).
  addTeamComment: (id, text)                => api.post(`/workspace/teams/${id}/comments`, { text }).then(r => r.data),
  // Bulk imports
  importRoster:   (rows)                    => api.post("/workspace/roster/import", rows).then(r => r.data),
  importOrgChart: (rows)                    => api.post("/workspace/org-chart/import", rows).then(r => r.data),
  // Person ↔ Workspace bridge
  tasksForPerson: (personId)                => api.get(`/workspace/persons/${personId}/tasks`).then(r => r.data),
  undoComplete:   (id)                      => api.post(`/workspace/tasks/${id}/undo-complete`).then(r => r.data),
  routineTick:    (id, note = "")           => api.post(`/workspace/tasks/${id}/routine-tick`, { note }).then(r => r.data),
  setQuiet:       (id, quiet)               => api.post(`/workspace/tasks/${id}/quiet`, { quiet }).then(r => r.data),
  bulk:           (task_ids, action, value) => api.post("/workspace/bulk", { task_ids, action, value }).then(r => r.data),
  bulkUndo:       (batch_id)                => api.post("/workspace/bulk/undo", { batch_id }).then(r => r.data),
  tidyPreview:    ()                        => api.get("/workspace/routines/tidy").then(r => r.data),
  tidy:           (catch_up)                => api.post("/workspace/routines/tidy", { catch_up }).then(r => r.data),
  overwatchAccess: ()                       => api.get("/workspace/overwatch/access").then(r => r.data),
  overwatchPlan:  (text, images)            => api.post("/workspace/overwatch/plan", { text, images }, { timeout: 120000 }).then(r => r.data),
  overwatchApply: (plan_id, exclude_ids)    => api.post("/workspace/overwatch/apply", { plan_id, exclude_ids }).then(r => r.data),
  routineUndo:    (tickId)                  => api.post(`/workspace/routine-ticks/${tickId}/undo`).then(r => r.data),
  routineTicks:   (id)                      => api.get(`/workspace/tasks/${id}/routine-ticks`).then(r => r.data),
  routinesPreview: ()                       => api.get("/workspace/routines/convert").then(r => r.data),
  routinesConvert: ()                       => api.post("/workspace/routines/convert").then(r => r.data),
  routinesUndo:   ()                        => api.post("/workspace/routines/convert/undo").then(r => r.data),
  checkin:        (id, on = true)           => api.post(`/workspace/tasks/${id}/checkin`, { on }).then(r => r.data),
  checkinAll:     ()                        => api.post("/workspace/tasks/checkin-all").then(r => r.data),
  summary:        (userId = null, light = false) => api.get("/me/summary", { params: { ...(userId && { user: userId }), ...(light && { light: 1 }) } }).then(r => r.data),
  // Users (borrowed from the existing endpoint for assignee dropdowns)
  listUsers:      ()                        => api.get("/users").then(r => r.data),
};

// Client-side stable sort by column.  Returns a NEW sorted array; original
// input is not mutated so React memoisation stays cheap.
export const FREQ_RANK = { every_30m: 0, hourly: 1, every_2h: 2, daily: 3, weekdays: 4, weekly: 5, monthly: 6, quarterly: 7, yearly: 8, ongoing: 9 };
export const taskRule = (t) => (t.recurrence?.rule && t.recurrence.rule !== "none" ? t.recurrence.rule : (t.recurrence_class && t.recurrence_class !== "none" ? t.recurrence_class : null));
export function sortTasks(rows, sortKey, sortDir) {
  if (!sortKey) return rows;
  const dir = sortDir === "desc" ? -1 : 1;
  const P = { low: 0, medium: 1, high: 2, urgent: 3 };
  const S = { todo: 0, in_progress: 1, waiting: 2, blocked: 3, done: 4, cancelled: 5 };
  return [...rows].sort((a, b) => {
    let va, vb;
    switch (sortKey) {
      case "priority": va = P[a.priority] ?? -1;      vb = P[b.priority] ?? -1;      break;
      case "status":   va = S[a.status]   ?? -1;      vb = S[b.status]   ?? -1;      break;
      case "due":      va = a.due_date || "9999";     vb = b.due_date || "9999";     break;
      case "assignee": va = a.assignee_id || "\uffff";vb = b.assignee_id || "\uffff";break;
      case "team":     va = a.team_id || "";          vb = b.team_id || "";          break;
      case "last":     va = a.last_activity_at || a.updated_at || ""; vb = b.last_activity_at || b.updated_at || ""; break;
      case "progress": va = progressOf(a).pct;      vb = progressOf(b).pct;        break;
      case "ref":      va = a.ref || "";             vb = b.ref || "";              break;
      case "title":    va = (a.title || "").toLowerCase(); vb = (b.title || "").toLowerCase(); break;
      case "type":     va = a.type || "";             vb = b.type || "";             break;
      case "freq":     va = FREQ_RANK[taskRule(a)] ?? 99; vb = FREQ_RANK[taskRule(b)] ?? 99; break;
      default:         va = 0; vb = 0;
    }
    if (va < vb) return -1 * dir;
    if (va > vb) return  1 * dir;
    return 0;
  });
}

// ─── Shared UI helpers ─────────────────────────────────────────────────
export const STATUS_TONE = {
  slate:   { bg: "bg-slate-500/15",   text: "text-slate-300",   border: "border-slate-500/40" },
  sky:     { bg: "bg-sky-500/15",     text: "text-sky-300",     border: "border-sky-500/40"   },
  rose:    { bg: "bg-rose-500/15",    text: "text-rose-300",    border: "border-rose-500/40"  },
  emerald: { bg: "bg-emerald-500/15", text: "text-emerald-300", border: "border-emerald-500/40" },
  amber:   { bg: "bg-amber-500/15",   text: "text-amber-300",   border: "border-amber-500/40" },
  purple:  { bg: "bg-purple-500/15",  text: "text-purple-300",  border: "border-purple-500/40" },
  teal:    { bg: "bg-teal-500/15",    text: "text-teal-300",    border: "border-teal-500/40"  },
  orange:  { bg: "bg-orange-500/15",  text: "text-orange-300",  border: "border-orange-500/40"},
  fuchsia: { bg: "bg-fuchsia-500/15", text: "text-fuchsia-300", border: "border-fuchsia-500/40"},
};

export const PRIORITY_TONE = {
  low:     "text-slate-400",
  medium:  "text-sky-300",
  high:    "text-amber-300",
  urgent:  "text-rose-300",
};

export function fmtDue(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const target = new Date(d); target.setHours(0, 0, 0, 0);
  const days = Math.round((target - today) / 86_400_000);
  if (days === 0)  return "Today";
  if (days === 1)  return "Tomorrow";
  if (days === -1) return "Yesterday";
  if (days > 0 && days < 7) return `In ${days}d`;
  if (days < 0 && days > -7) return `${Math.abs(days)}d overdue`;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

export function isOverdue(task) {
  if (!task?.due_date || task.status === "done") return false;
  const d = new Date(task.due_date);
  if (isNaN(d)) return false;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return d < today;
}

// ─── Scope (derived, no migration) ──────────────────────────────────────
// personal = no team · management = team named/kind "management" · team = else
export function scopeOf(task, teamMap = {}) {
  if (!task?.team_id) return "personal";
  const t = teamMap[task.team_id];
  const name = (t?.name || "").toLowerCase();
  if (t?.kind === "management" || name.includes("management")) return "management";
  return "team";
}

export const SCOPE_META = {
  personal:   { label: "Personal", cls: "bg-slate-500/15 text-slate-300 border-slate-500/40" },
  team:       { label: "Team",     cls: "bg-teal-500/15 text-teal-300 border-teal-500/40" },
  management: { label: "Mgmt",     cls: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
};

export function isRecurring(task) {
  return !!task?.recurrence_class && task.recurrence_class !== "none";
}

// "needs attention" score for default Overwatch ordering: overdue → blocked → rest
export function attentionRank(task) {
  if (isOverdue(task)) return 0;
  if (task?.status === "blocked") return 1;
  return 2;
}

// ─── Rhythm (recurrence) — pills + per-period ticking ──────────────────
export const RHYTHM = [
  { key: "none",    label: "One-off", cls: "bg-slate-500/15 text-slate-300 border-slate-500/40" },
  { key: "daily",   label: "Daily",   cls: "bg-sky-500/15 text-sky-300 border-sky-500/40" },
  { key: "weekly",  label: "Weekly",  cls: "bg-violet-500/15 text-violet-300 border-violet-500/40" },
  { key: "monthly", label: "Monthly", cls: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/40" },
  { key: "ongoing", label: "Ongoing", cls: "bg-teal-500/15 text-teal-300 border-teal-500/40" },
];
export const RHYTHM_META = Object.fromEntries(RHYTHM.map((r) => [r.key, r]));

function weekStart(d) {
  const x = new Date(d); x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x.getTime();
}

// Was `iso` inside the current period for this rhythm? (one-off/ongoing → any time)
export function doneThisPeriod(iso, rhythm) {
  if (!iso) return false;
  const d = new Date(iso), n = new Date();
  if (rhythm === "daily") return d.toDateString() === n.toDateString();
  if (rhythm === "weekly") return weekStart(d) === weekStart(n);
  if (rhythm === "monthly") return d.getMonth() === n.getMonth() && d.getFullYear() === n.getFullYear();
  return true;
}

export const tickTask = (id, done) => api.post(`/workspace/tasks/${id}/tick`, { done }).then((r) => r.data);

// ─── V2 shared helpers (11-00) ─────────────────────────────────────────
export const isMgmt = (user) => !!(user?.super_admin || user?.system_admin || user?.is_admin
  || ["admin", "owner", "system_admin", "manager", "management"].includes((user?.role || "").toLowerCase())
  || ["admin", "manager"].includes(user?.access_role || "")
  || ["owner", "manager", "system_admin"].includes((user?.role_workspace || "").toLowerCase()));

export const userKey = (user) => user?.id || user?._id || user?.email || "";

const DAY = 86_400_000;
const startOf = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };

// Absolute UK date; year shown whenever it isn't the current year (11-00 §5).
export function absDate(iso, withWeekday = false) {
  if (!iso) return "";
  const d = new Date(String(iso).length <= 10 ? `${iso}T12:00:00` : iso);
  if (isNaN(d)) return String(iso);
  const opts = { day: "numeric", month: "short", ...(withWeekday && { weekday: "short" }) };
  if (d.getFullYear() !== new Date().getFullYear()) opts.year = "numeric";
  return d.toLocaleDateString("en-GB", opts);
}

export function relAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso); if (isNaN(d)) return "—";
  const days = Math.round((startOf(new Date()) - startOf(d)) / DAY);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 365) return `${days}d ago`;
  return absDate(iso);
}

// {text, sub, tone} for a task's due date. Done/cancelled/monitor never overdue.
export function dueInfo(task) {
  if (!task?.due_date) return { text: task?.type === "monitor" ? "Ongoing" : "No date", sub: "", tone: "muted" };
  const d = new Date(`${String(task.due_date).slice(0, 10)}T12:00:00`);
  const days = Math.round((startOf(d) - startOf(new Date())) / DAY);
  const closed = ["done", "cancelled"].includes(task.status) || task.type === "monitor";
  const sub = absDate(task.due_date);
  const time = task.due_time ? ` ${task.due_time}` : "";
  if (days < 0 && !closed) return { text: `${-days}d overdue`, sub, tone: "red", days };
  if (days === 0) return { text: `Today${time}`, sub, tone: closed ? "muted" : "amber", days };
  if (days === 1) return { text: `Tomorrow${time}`, sub, tone: "normal", days };
  if (days > 0) return { text: `In ${days}d`, sub, tone: "normal", days };
  return { text: `${-days}d ago`, sub, tone: "muted", days };
}

export function progressOf(task) {
  const list = task?.checklist || [];
  const done = list.filter((c) => c.done).length;
  return { done, total: list.length, pct: list.length ? Math.round((done / list.length) * 100) : -1 };
}

export const pinnedLink = (task) => (task?.links || []).find((l) => l.pinned) || null;

export function checkedInThisWeek(task, uid) {
  const now = new Date(); const t = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  const day = t.getUTCDay() || 7; t.setUTCDate(t.getUTCDate() + 4 - day);
  const y = t.getUTCFullYear(); const wk = Math.ceil(((t - Date.UTC(y, 0, 1)) / DAY + 1) / 7);
  const key = `${y}-W${String(wk).padStart(2, "0")}`;
  return (task?.acks || []).some((a) => a.user_id === uid && a.week === key);
}

export const TASK_CHANGED = "talmia:tasks-changed";
export const emitTasksChanged = () => window.dispatchEvent(new Event(TASK_CHANGED));

// /api/users omits some admin accounts — make sure the signed-in user is always resolvable by id.
export const withMe = (users = [], user) => {
  const id = userKey(user);
  return !id || users.some((u) => u.id === id) ? users : [{ id, name: user?.name || "Me", email: user?.email }, ...users];
};

const NAMED = { slate: "#94a3b8", teal: "#2dd4bf", sky: "#38bdf8", blue: "#60a5fa", indigo: "#818cf8", violet: "#a78bfa", fuchsia: "#e879f9", pink: "#f472b6", rose: "#fb7185", orange: "#fb923c", amber: "#fbbf24", lime: "#a3e635", emerald: "#34d399", purple: "#c084fc", red: "#f87171", green: "#4ade80", yellow: "#facc15", cyan: "#22d3ee", gray: "#9ca3af" };
export const TEAM_SWATCHES = Object.values(NAMED);
// Light mode: deepen bright hexes so team/type colours stay readable on white.
export const inkHex = (hex) => {
  if (typeof document === "undefined" || document.documentElement.getAttribute("data-talmia-theme") !== "light" || !/^#[0-9a-f]{6}$/i.test(hex || "")) return hex;
  const n = parseInt(hex.slice(1), 16), f = 0.5;
  return "#" + [16, 8, 0].map((s) => Math.round(((n >> s) & 255) * f).toString(16).padStart(2, "0")).join("");
};
export const inkMap = (m) => new Proxy(m, { get: (o, k) => (typeof o[k] === "string" ? inkHex(o[k]) : o[k]?.hex ? { ...o[k], hex: inkHex(o[k].hex) } : o[k]) });
export const teamColor = (t) => { const c = t?.color || "teal"; return inkHex(c.startsWith("#") ? c : (NAMED[c] || NAMED.teal)); };
